import type { ServiceStaff } from "@/types/appointments";
import type { AppointmentAccent } from "./types";

interface MiniCalendarDay {
  date: Date | null;
  key: string;
  label: number | null;
}

const dayNameFormatter = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
});

const monthYearFormatter = new Intl.DateTimeFormat("en-US", {
  month: "long",
  year: "numeric",
});

const fullDateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "long",
  day: "numeric",
  year: "numeric",
});

const monthFormatter = new Intl.DateTimeFormat("en-US", {
  month: "long",
});

const shortDateFormatter = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "numeric",
});

const timeFormatter = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

const categoryAccentOrder: Record<string, Array<keyof typeof accentMap>> = {
  Doctor: ["primary", "accent"],
  Nutritionist: ["muted", "neutral"],
  Physiotherapist: ["neutralStrong"],
};

const accentMap = {
  primary: {
    badgeClassName: "border-primary/15 bg-primary/10 text-primary",
    cardClassName:
      "border-primary/15 bg-primary/10 text-foreground hover:bg-primary/15",
    dotClassName: "bg-primary",
  },
  accent: {
    badgeClassName: "border-border bg-accent text-foreground",
    cardClassName: "border-border bg-accent text-foreground hover:bg-accent/80",
    dotClassName: "bg-foreground/55",
  },
  muted: {
    badgeClassName: "border-border bg-muted text-foreground",
    cardClassName: "border-border bg-muted/80 text-foreground hover:bg-muted",
    dotClassName: "bg-muted-foreground",
  },
  neutral: {
    badgeClassName: "border-border bg-card text-foreground",
    cardClassName: "border-border bg-card text-foreground hover:bg-accent/50",
    dotClassName: "bg-foreground/45",
  },
  neutralStrong: {
    badgeClassName: "border-border bg-secondary text-secondary-foreground",
    cardClassName:
      "border-border bg-secondary text-foreground hover:bg-secondary/80",
    dotClassName: "bg-foreground/65",
  },
  slate: {
    badgeClassName: "border-border bg-muted text-foreground",
    cardClassName: "border-border bg-muted/80 text-foreground hover:bg-muted",
    dotClassName: "bg-muted-foreground",
  },
} satisfies Record<string, AppointmentAccent>;

const fallbackAccent = accentMap.slate;

export const DEFAULT_EXPANDED_SECTIONS = ["Services", "Staff"];
export const STAFF_CATEGORY_ORDER = [
  "Doctor",
  "Nutritionist",
  "Physiotherapist",
];
export const WEEKDAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];

export const formatDateForAPI = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getWeekRange = (date: Date): { start: Date; end: Date } => {
  const current = new Date(date);
  const day = current.getDay();
  const diff = current.getDate() - day;
  const start = new Date(current.setDate(diff));
  start.setHours(0, 0, 0, 0);

  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);

  return { start, end };
};

export const getDayRange = (date: Date): { start: Date; end: Date } => {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);

  const end = new Date(date);
  end.setHours(23, 59, 59, 999);

  return { start, end };
};

export const getWeekDays = (startDate: Date): Date[] =>
  Array.from({ length: 7 }, (_, index) => {
    const day = new Date(startDate);
    day.setDate(startDate.getDate() + index);
    return day;
  });

export const buildMiniCalendarDays = (currentDate: Date): MiniCalendarDay[] => {
  const firstDay = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  );
  const lastDay = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  );
  const startDay = firstDay.getDay();
  const daysInMonth = lastDay.getDate();
  const days: MiniCalendarDay[] = [];

  for (let index = 0; index < startDay; index += 1) {
    days.push({ date: null, key: `empty-${index}`, label: null });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );

    days.push({
      date,
      key: `${currentDate.getFullYear()}-${currentDate.getMonth()}-${day}`,
      label: day,
    });
  }

  return days;
};

export const formatMonthYear = (date: Date): string =>
  monthYearFormatter.format(date);

export const formatWeekPeriodLabel = (
  startDate: Date,
  endDate: Date
): string => {
  const startMonth = monthFormatter.format(startDate);
  const endMonth = monthFormatter.format(endDate);
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();

  if (startMonth === endMonth && startYear === endYear) {
    return formatMonthYear(startDate);
  }

  if (startYear === endYear) {
    return `${startMonth}-${endMonth} ${startYear}`;
  }

  return `${startMonth} ${startYear}-${endMonth} ${endYear}`;
};

export const formatDayPeriodLabel = (date: Date): string =>
  fullDateFormatter.format(date);

export const formatShortDate = (dateString: string): string =>
  shortDateFormatter.format(new Date(dateString));

export const formatTime = (dateString: string): string =>
  timeFormatter.format(new Date(dateString));

export const formatCompactTimeRange = (
  startDateString: string,
  endDateString: string
): string => {
  const [startTime, startMeridiem] = formatTime(startDateString).split(" ");
  const [endTime, endMeridiem] = formatTime(endDateString).split(" ");

  if (startMeridiem && endMeridiem && startMeridiem === endMeridiem) {
    return `${startTime}-${endTime} ${endMeridiem}`;
  }

  return `${formatTime(startDateString)}-${formatTime(endDateString)}`;
};

export const formatDayLabel = (date: Date): string =>
  dayNameFormatter.format(date).toUpperCase();

export const createTimeSlots = (): string[] =>
  Array.from({ length: 24 }, (_, hour) => {
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour} ${hour < 12 ? "AM" : "PM"}`;
  });

export const getSlotKey = (date: Date, hour: number): string =>
  `${formatDateForAPI(date)}-${hour}`;

export const getAppointmentDurationHours = (
  startTime: string,
  endTime: string
): number => {
  const start = new Date(startTime);
  const end = new Date(endTime);
  return (end.getTime() - start.getTime()) / (1000 * 60 * 60);
};

export const getStaffAccent = (
  _staffId: string,
  staffIndex: number,
  category: string
): AppointmentAccent => {
  const accentKeys = categoryAccentOrder[category] ?? ["slate"];
  const accentKey = accentKeys[staffIndex % accentKeys.length] ?? "slate";

  return accentMap[accentKey] ?? fallbackAccent;
};

export const getAppointmentAccent = (
  staffId: string,
  serviceStaffList: ServiceStaff[]
): AppointmentAccent => {
  const staff = serviceStaffList.find(
    (member) => member.doctor__id === staffId
  );
  if (!staff) {
    return fallbackAccent;
  }

  const categoryStaff = serviceStaffList.filter(
    (member) => member.category === staff.category
  );
  const staffIndex = categoryStaff.findIndex(
    (member) => member.doctor__id === staff.doctor__id
  );

  return getStaffAccent(staffId, Math.max(staffIndex, 0), staff.category);
};

export const getStaffCategories = (
  serviceStaffList: ServiceStaff[]
): string[] => {
  const categories = Array.from(
    new Set(serviceStaffList.map((staff) => staff.category))
  );
  const ordered = STAFF_CATEGORY_ORDER.filter((category) =>
    categories.includes(category)
  );
  const remaining = categories
    .filter((category) => !STAFF_CATEGORY_ORDER.includes(category))
    .sort((left, right) => left.localeCompare(right));

  return [...ordered, ...remaining];
};

export const toTitleCase = (value: string): string =>
  value
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map(
      (segment) => segment[0]?.toUpperCase() + segment.slice(1).toLowerCase()
    )
    .join(" ");
